import { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, ShieldCheck, Coins, Layers, PenLine, TrendingUp, Crown, Sparkles, CheckCircle2, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BENEFITS = [
  [Sparkles, "Your Official Player Cards", "We create high-quality, collectible digital cards featuring your likeness, play style, and performance metrics — art-directed by the Bullpen, approved by you."],
  [Coins, "Marketplace Earnings", "Every time your card is bought or sold, you receive a percentage of that transaction. Your name working for you — on every trade, forever."],
  [Layers, "Pack Royalties", "When your card is pulled in a rip pack, you earn a royalty — meaning you get paid even when fans are chasing your card."],
  [PenLine, "Digital Signature Registry", "Fans can get their cards digitally signed by you during or after tournaments. Every signature is permanently stored in our official registry — verified, collectible scarcity tied directly to you. Each card can only ever be signed once."],
  [TrendingUp, "Performance-Driven Value", "As your skill, ranking, and visibility increase, so does the demand and value of your card. You're not just playing for points — you're building an asset."],
  [Crown, "Early Athlete Advantage", "As a founding athlete, your cards become part of the first generation of the system — which historically holds the highest long-term value in any collectible market."],
];

const INV_CSS = `
@import url('https://cdn.jsdelivr.net/npm/dseg@0.46.3/css/dseg.css');
@keyframes invCharFlash{0%{color:#fff;text-shadow:0 0 16px #fff,0 0 30px #67e8f9}100%{color:#fbbf24;text-shadow:0 0 6px rgba(251,191,36,.6)}}
.inv-char{color:#fbbf24;text-shadow:0 0 6px rgba(251,191,36,.5)}
.inv-char-new{animation:invCharFlash .32s ease-out both}
@keyframes invLaserBlink{0%,100%{opacity:1}50%{opacity:.25}}
.inv-laser{display:inline-block;width:.6em;height:1.1em;vertical-align:text-bottom;background:#e8fbff;box-shadow:0 0 10px #67e8f9,0 0 24px #22d3ee,0 0 44px rgba(34,211,238,.6);animation:invLaserBlink .5s step-end infinite;position:relative}
.inv-laser::before{content:"";position:absolute;left:50%;bottom:100%;width:2px;height:60vh;transform:translateX(-50%);background:linear-gradient(180deg,transparent,rgba(103,232,249,.5) 70%,#a5f3fc);pointer-events:none}
.inv-parch{position:relative;margin:0 5.5%;background:linear-gradient(180deg,rgba(13,20,24,.97),rgba(6,10,12,.97));border-left:1px solid rgba(45,212,191,.4);border-right:1px solid rgba(45,212,191,.4);box-shadow:inset 0 0 60px rgba(0,0,0,.85),0 0 40px rgba(34,211,238,.12)}
.inv-parch::before{content:"";position:absolute;inset:0;pointer-events:none;background:repeating-linear-gradient(0deg,rgba(255,255,255,.018) 0 1px,transparent 1px 3px)}
.inv-parch::after{content:"";position:absolute;inset:0;pointer-events:none;background:radial-gradient(120% 60% at 50% 0%,rgba(45,212,191,.07),transparent 55%)}
@keyframes invScanBar{0%{top:-4%}100%{top:102%}}
@keyframes invCardIn{0%{clip-path:inset(0 0 100% 0);filter:brightness(3.5) saturate(0) contrast(2)}50%{clip-path:inset(0 0 0 0);filter:brightness(1.9) saturate(.5) contrast(1.5)}62%{filter:brightness(1)}70%{filter:brightness(2.4) hue-rotate(25deg)}76%{filter:brightness(1)}84%{filter:brightness(1.7)}100%{clip-path:inset(0);filter:brightness(1)}}
@keyframes invGlitch{0%{clip-path:inset(8% 0 76% 0);transform:translateX(-6px);opacity:.8}20%{clip-path:inset(52% 0 30% 0);transform:translateX(5px)}40%{clip-path:inset(24% 0 58% 0);transform:translateX(-4px)}60%{clip-path:inset(70% 0 12% 0);transform:translateX(6px);opacity:.6}80%{clip-path:inset(40% 0 44% 0);transform:translateX(-3px);opacity:.4}100%{opacity:0}}
@keyframes invFlare{0%{opacity:0}30%{opacity:.9}100%{opacity:0}}
.inv-card-img{animation:invCardIn 2.3s ease-out both}
.inv-card-glitch{position:absolute;inset:0;animation:invGlitch 1.7s steps(8) both;pointer-events:none}
.inv-scanbar{position:absolute;left:-6%;right:-6%;height:3px;background:linear-gradient(90deg,transparent,#a5f3fc,transparent);box-shadow:0 0 18px #22d3ee;animation:invScanBar 2.3s ease-out both;pointer-events:none}
.inv-flare{position:absolute;inset:-30%;background:radial-gradient(closest-side,rgba(165,243,252,.5),transparent 70%);animation:invFlare .9s ease-out 2.1s both;pointer-events:none}
@keyframes invBtnIn{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
@keyframes invYesPulse{0%,100%{box-shadow:0 0 14px rgba(250,204,21,.5),0 0 34px rgba(250,204,21,.25)}50%{box-shadow:0 0 30px rgba(250,204,21,.9),0 0 60px rgba(250,204,21,.4)}}
.inv-yes{animation:invBtnIn .7s ease-out both,invYesPulse 2.2s ease-in-out .7s infinite}
@keyframes invBarShoot{0%{width:0;opacity:0}15%{opacity:1}70%{width:80%}100%{width:75%}}
.inv-bar{height:3px;background:linear-gradient(90deg,transparent,#a5f3fc 15%,#fff 50%,#a5f3fc 85%,transparent);box-shadow:0 0 16px #22d3ee,0 0 42px rgba(34,211,238,.6);animation:invBarShoot 1.1s cubic-bezier(.2,.9,.2,1) both}
@keyframes invSheetPan{from{transform:translateY(-101%)}to{transform:translateY(0)}}
.inv-sheet{transform:translateY(-101%);animation:invSheetPan 1.4s cubic-bezier(.3,.8,.3,1) .9s both;background:linear-gradient(180deg,rgba(10,16,20,.97),rgba(5,9,11,.98));border:1px solid rgba(45,212,191,.35);border-top:0;box-shadow:0 0 50px rgba(34,211,238,.15),inset 0 0 80px rgba(0,0,0,.7)}
.inv-mx-scramble{color:#4ade80;text-shadow:0 0 8px rgba(74,222,128,.8)}
@keyframes invUnrollGlow{0%,100%{opacity:.5}50%{opacity:1}}
`;

const GLYPHS = "アイウエオカキクケコ01<>#$%&@*+=";

let _invCtx;
const actx = () => (_invCtx = _invCtx || new (window.AudioContext || window.webkitAudioContext)());
const sndTick = () => {
  try {
    const c = actx();
    if (c.state !== "running") return;
    const o = c.createOscillator(), g = c.createGain();
    o.type = "square";
    o.frequency.value = 2300 + Math.random() * 1000;
    g.gain.setValueAtTime(0.022, c.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + 0.03);
    o.connect(g); g.connect(c.destination);
    o.start(); o.stop(c.currentTime + 0.035);
  } catch { /* noop */ }
};
const sndChord = () => {
  try {
    const c = actx();
    if (c.state !== "running") return;
    const t = c.currentTime;
    const master = c.createGain();
    master.gain.setValueAtTime(0.0001, t);
    master.gain.exponentialRampToValueAtTime(0.2, t + 0.06);
    master.gain.exponentialRampToValueAtTime(0.0001, t + 1.6);
    master.connect(c.destination);
    [82.41, 123.47, 164.81, 246.94].forEach((f, i) => {
      const o = c.createOscillator(), g = c.createGain();
      o.type = "sawtooth";
      o.frequency.value = f * (1 + (i % 2 ? 0.004 : -0.004));
      g.gain.value = 0.22;
      o.connect(g); g.connect(master);
      o.start(t); o.stop(t + 1.7);
    });
    const s = c.createOscillator(), sg = c.createGain();
    s.type = "triangle";
    s.frequency.setValueAtTime(1318.5, t);
    s.frequency.exponentialRampToValueAtTime(659.25, t + 0.9);
    sg.gain.setValueAtTime(0.05, t);
    sg.gain.exponentialRampToValueAtTime(0.0001, t + 1.0);
    s.connect(sg); sg.connect(master);
    s.start(t); s.stop(t + 1.1);
  } catch { /* noop */ }
};

function MatrixText({ text, delay = 0, speed = 3, className = "", style, tag: Tag = "span" }) {
  const [p, setP] = useState(-1);
  useEffect(() => {
    const t = setTimeout(() => {
      setP(0);
      const iv = setInterval(() => setP((x) => {
        if (x >= text.length + 8) { clearInterval(iv); return x; }
        return x + speed;
      }), 24);
    }, delay);
    return () => clearTimeout(t);
  }, [text, delay, speed]);
  if (p < 0) return <Tag className={className} style={{ ...style, visibility: "hidden" }}>{text}</Tag>;
  return (
    <Tag className={className} style={style}>
      {text.split("").map((ch, i) => {
        if (i < p - 7 || p >= text.length + 8) return <span key={i}>{ch}</span>;
        if (i < p) return <span key={i} className="inv-mx-scramble">{ch === " " ? " " : GLYPHS[(i * 7 + p) % GLYPHS.length]}</span>;
        return <span key={i} style={{ visibility: "hidden" }}>{ch}</span>;
      })}
    </Tag>
  );
}

function RainCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf, cols, drops;
    const setup = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      cols = Math.ceil(canvas.width / 16);
      drops = Array.from({ length: cols }, () => Math.random() * -(canvas.height / 16));
    };
    setup();
    const draw = () => {
      ctx.fillStyle = "rgba(5,9,11,.14)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = "12px monospace";
      for (let i = 0; i < cols; i++) {
        ctx.fillStyle = "rgba(74,222,128,.35)";
        ctx.fillText(GLYPHS[Math.floor(Math.random() * GLYPHS.length)], i * 16, drops[i] * 16);
        drops[i] = drops[i] * 16 > canvas.height && Math.random() > 0.975 ? 0 : drops[i] + 0.5;
      }
      raf = requestAnimationFrame(draw);
    };
    draw();
    window.addEventListener("resize", setup);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", setup); };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full opacity-40 pointer-events-none" />;
}

function LaserScroll({ data, onYes }) {
  const msg = (data.message || "").toUpperCase();
  const [unrolled, setUnrolled] = useState(false);
  const [n, setN] = useState(0);
  const [cardIn, setCardIn] = useState(false);
  const [showYes, setShowYes] = useState(false);
  const nRef = useRef(0);
  useEffect(() => {
    const t = setTimeout(() => setUnrolled(true), 400);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    if (!unrolled) return;
    const delayPer = Math.max(16, Math.min(45, Math.round(13000 / Math.max(msg.length, 1))));
    let iv;
    const t0 = setTimeout(() => {
      iv = setInterval(() => {
        if (nRef.current >= msg.length) { clearInterval(iv); return; }
        const ch = msg[nRef.current];
        nRef.current += 1;
        if (ch && ch.trim()) sndTick();
        setN(nRef.current);
      }, delayPer);
    }, 2100);
    return () => { clearTimeout(t0); clearInterval(iv); };
  }, [unrolled, msg]);
  const doneTyping = n >= msg.length && msg.length > 0;
  useEffect(() => {
    if (!doneTyping) return;
    const t1 = setTimeout(() => setCardIn(true), 600);
    const tc = data.card ? setTimeout(sndChord, 2650) : null;
    const t2 = setTimeout(() => setShowYes(true), data.card ? 3300 : 1200);
    return () => { clearTimeout(t1); clearTimeout(t2); if (tc) clearTimeout(tc); };
  }, [doneTyping, data.card]);
  return (
    <div className="relative w-[min(94vw,600px)] mx-auto pb-16" data-testid="invite-scroll">
      {!doneTyping && n > 4 && (
        <button data-testid="invite-skip" onClick={() => { nRef.current = msg.length; setN(msg.length); }}
          className="absolute -top-8 right-0 text-[9px] font-black uppercase tracking-widest text-zinc-600 hover:text-zinc-300">Skip ≫</button>
      )}
      <img src="/icons/scroll-roller.png" alt="" className="relative z-10 w-full drop-shadow-[0_6px_18px_rgba(0,0,0,.8)]"
        style={{ animation: "invUnrollGlow 3s ease-in-out infinite" }} />
      <div className="inv-parch overflow-hidden transition-[max-height] duration-[2000ms] ease-out"
        style={{ maxHeight: unrolled ? "1600px" : "0px" }}>
        <div className="relative z-[1] px-5 sm:px-8 py-8 min-h-[380px]">
          <div className="text-[8px] font-black uppercase tracking-[0.45em] text-teal-300/70 mb-6 text-center">◈ Bullpen Transmission · Private · {data.name} ◈</div>
          <div data-testid="invite-message" className="text-[10px] sm:text-[13px] leading-[2.1] whitespace-pre-wrap break-words"
            style={{ fontFamily: '"DSEG14 Classic", "DSEG7 Classic", monospace' }}>
            {msg.slice(0, n).split("").map((ch, i) => (
              <span key={i} className={i === n - 1 ? "inv-char inv-char-new" : "inv-char"}>{ch}</span>
            ))}
            {!doneTyping && <span className="inv-laser" data-testid="invite-laser" />}
          </div>
          {cardIn && data.card && (
            <div className="mt-8 flex flex-col items-center" data-testid="invite-card-materialize">
              <div className="relative w-44 sm:w-56">
                <img src={data.card.url} alt={data.card.name} className="inv-card-img w-full border border-teal-400/40" />
                <img src={data.card.url} alt="" aria-hidden className="inv-card-glitch w-full border border-transparent" />
                <div className="inv-scanbar" />
                <div className="inv-flare" />
              </div>
              <div className="mt-3 text-[9px] font-black uppercase tracking-[0.35em] text-teal-300" style={{ animation: "invBtnIn .6s ease-out 2s both" }}>
                {data.card.name} · Forged for you
              </div>
            </div>
          )}
          {showYes && (
            <div className="mt-8 flex justify-center">
              <button data-testid="invite-yes-btn" onClick={onYes}
                className="inv-yes bg-yellow-400 text-black font-display font-black uppercase tracking-[0.3em] text-sm px-10 py-4 hover:bg-yellow-300 active:scale-95 transition-transform">
                Yes, I'm In
              </button>
            </div>
          )}
        </div>
      </div>
      <img src="/icons/scroll-roller.png" alt="" className="relative z-10 w-full rotate-180 -mt-0.5 drop-shadow-[0_10px_22px_rgba(0,0,0,.9)]" />
    </div>
  );
}

function PitchReveal({ data }) {
  const [stage, setStage] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 300);
    const t2 = setTimeout(() => setStage(2), 2400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);
  const first = (data.name || "Athlete").split(" ")[0];
  let d = 0;
  const next = (ms) => { const v = d; d += ms; return v; };
  return (
    <div className="fixed inset-0 z-[95] bg-black overflow-y-auto overflow-x-hidden" data-testid="invite-pitch">
      <div className="pt-12 pb-20">
        {stage >= 1 && <div className="inv-bar mx-auto" data-testid="invite-bar" />}
        {stage >= 1 && (
          <div className="mx-auto w-[min(92vw,75%)] overflow-hidden">
            <div className="inv-sheet relative" data-testid="invite-sheet">
              <RainCanvas />
              <div className="relative z-[1] px-6 sm:px-10 py-10">
                {stage >= 2 && (
                  <>
                    <MatrixText tag="div" text="FOUNDING ATHLETE BRIEFING" delay={next(400)} speed={2}
                      className="text-[10px] font-black uppercase tracking-[0.4em] text-teal-300 mb-4" />
                    <MatrixText tag="h1" text={`Welcome to the inside, ${first}.`} delay={next(700)} speed={2}
                      className="font-display font-black uppercase text-2xl sm:text-4xl leading-tight mb-5 text-white" />
                    <MatrixText tag="p" text="This isn't just a trading card. This is ownership, exposure, and recurring income tied directly to your performance, your name, and your growth in the sport." delay={next(900)} speed={4}
                      className="block text-sm text-zinc-300 max-w-2xl leading-relaxed mb-3" />
                    <MatrixText tag="p" text="We've built a performance-driven digital card ecosystem inside Pickle Bullpen where athletes aren't just featured — they participate in the upside." delay={next(900)} speed={4}
                      className="block text-sm text-zinc-400 max-w-2xl leading-relaxed mb-8" />
                    {data.note && (
                      <MatrixText tag="p" text={`"${data.note}"`} delay={next(700)} speed={4}
                        className="block border-l-2 border-yellow-400 pl-4 text-sm text-zinc-300 italic max-w-2xl mb-8" />
                    )}
                    <MatrixText tag="div" text="— HERE'S HOW IT WORKS —" delay={next(500)} speed={2}
                      className="text-[10px] font-black uppercase tracking-[0.35em] text-zinc-500 mb-5" />
                    <div className="grid sm:grid-cols-2 gap-4 mb-10">
                      {BENEFITS.map(([Icon, title, desc], i) => (
                        <div key={title} className="border border-teal-500/20 bg-black/50 p-4" data-testid={`invite-benefit-${i}`}>
                          <Icon className="w-4 h-4 text-yellow-400 mb-2" />
                          <MatrixText tag="div" text={title.toUpperCase()} delay={next(350)} speed={2}
                            className="font-display font-bold text-xs mb-1.5 text-white" />
                          <MatrixText tag="p" text={desc} delay={next(250)} speed={6}
                            className="block text-[11px] text-zinc-400 leading-relaxed" />
                        </div>
                      ))}
                    </div>
                    <MatrixText tag="div" text="THIS IS BIGGER THAN CARDS." delay={next(500)} speed={2}
                      className="font-display font-black uppercase text-xl sm:text-2xl text-white leading-tight" />
                    <MatrixText tag="div" text="THIS IS BUILDING YOUR LEGACY WHILE YOU'RE STILL PLAYING." delay={next(700)} speed={2}
                      className="font-display font-black uppercase text-xl sm:text-2xl text-yellow-400 leading-tight mb-8" />
                    <div className="border border-emerald-500/40 bg-emerald-500/5 p-5 max-w-2xl" style={{ animation: `invBtnIn .8s ease-out ${next(600) / 1000}s both` }} data-testid="invite-confirm">
                      <div className="flex items-center gap-3 text-emerald-300">
                        <CheckCircle2 className="w-6 h-6 shrink-0" />
                        <div>
                          <div className="font-display font-black uppercase">You're on the list, {first}.</div>
                          <div className="text-xs text-zinc-400 mt-0.5">The Bullpen has been notified — we'll reach out to walk you through your card designs and revenue structure.</div>
                        </div>
                      </div>
                      <a href="/auth" data-testid="invite-signin-btn"
                        className="mt-4 inline-flex items-center gap-2 bg-yellow-400 text-black font-black uppercase tracking-[0.18em] text-xs px-5 py-3 hover:bg-[#BEF264] active:scale-95 transition-all"
                        style={{ boxShadow: "0 0 18px rgba(250,204,21,.45), 0 0 40px rgba(250,204,21,.2)" }}>
                        Enter the Bullpen — Create Your Athlete Account →
                      </a>
                      <div className="flex items-center gap-2 text-xs text-zinc-500 mt-4">
                        <Phone className="w-3.5 h-3.5" /> Or text us directly: <a href="sms:9418995300" className="text-yellow-300 font-bold">941-899-5300</a>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AthleteInvite() {
  const { token } = useParams();
  const location = useLocation();
  const isPreview = new URLSearchParams(location.search).get("preview") === "1";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [phase, setPhase] = useState("scroll");
  const [soundOn, setSoundOn] = useState(false);

  useEffect(() => {
    const unlock = () => {
      try { actx().resume().then(() => setSoundOn(true)); } catch { /* noop */ }
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    window.addEventListener("touchstart", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      window.removeEventListener("touchstart", unlock);
    };
  }, []);

  useEffect(() => {
    axios.get(`${API}/invite/${token}${isPreview ? "?preview=1" : ""}`)
      .then(({ data }) => setData(data))
      .catch((e) => setError(e.response?.data?.detail || "This invitation link isn't valid"));
  }, [token, isPreview]);

  const onYes = async () => {
    if (!isPreview) {
      try { await axios.post(`${API}/invite/${token}/interest`); } catch { /* noop */ }
    }
    setPhase("pitch");
  };

  if (error) return (
    <div className="min-h-screen bg-black flex items-center justify-center p-6">
      <div className="text-center" data-testid="invite-invalid">
        <ShieldCheck className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
        <div className="text-zinc-400 text-sm">{error}</div>
      </div>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <Loader2 className="w-6 h-6 text-yellow-400 animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white" data-testid="athlete-invite-page">
      <style>{INV_CSS}</style>
      {isPreview && (
        <div className="fixed top-0 inset-x-0 z-[99] bg-yellow-400 text-black text-center text-[9px] font-black uppercase tracking-[0.3em] py-1" data-testid="invite-preview-banner">
          Admin Preview — views & interest are not recorded
        </div>
      )}
      {!soundOn && phase === "scroll" && (
        <div className="fixed bottom-3 inset-x-0 z-[98] text-center text-[9px] uppercase tracking-[0.3em] text-zinc-600 pointer-events-none" data-testid="invite-sound-hint">
          🔊 tap anywhere to enable sound
        </div>
      )}

      {phase === "scroll" && (
        <div className="relative min-h-screen flex items-start justify-center pt-16 sm:pt-20 overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(80%_50%_at_50%_0%,rgba(34,211,238,.07),transparent_60%)] pointer-events-none" />
          <LaserScroll data={data} onYes={onYes} />
        </div>
      )}
      {phase === "pitch" && <PitchReveal data={data} />}
    </div>
  );
}
